import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FormattedMessage } from '@futrli/localisation';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { useRouteIds } from '../../../hooks/useRouteIds';
import { ExportedPdfRowItem } from '../tabs/exported-pdfs/types';

interface DeleteExportedPdfDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  item: ExportedPdfRowItem;
}

export const DeleteExportedPdfDialogContainer = ({ isOpen, onClose, item }: DeleteExportedPdfDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(
      () =>
        new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
          schedules: false,
          awaitingReview: false,
          exported: false
        }),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const { screen, handleAction: handleDelete, handleClose } = useDialogWizardState({
    isLoading: view.state.isDeletingExported,
    error: view.state.errorDeletingExported,
    onAction: () => view.deleteExported({ exportedReportId: item.id }),
    onClose
  });

  const title = intl.formatMessage({ id: 'reporting.delete_pdf', defaultMessage: 'Delete PDF' });

  const deleteDescription = intl.formatMessage({
    id: 'reporting.confirm_delete_pdf',
    defaultMessage: 'Are you sure you want to delete this PDF?'
  });

  const thingsToKnow = [
    <FormattedMessage
      key="unrecoverable"
      id="reporting.delete_pdf_things_to_know_unrecoverable"
      defaultMessage="Deleting a PDF is not recoverable"
    />,
    <FormattedMessage
      key="access"
      id="reporting.delete_pdf_things_to_know_access"
      defaultMessage="All users will immediately lose access to this PDF"
    />
  ];

  const loadingMessage = intl.formatMessage({
    id: 'reporting.deleting_pdf',
    defaultMessage: 'Deleting PDF...'
  });

  const successMessage = intl.formatMessage({
    id: 'reporting.pdf_deleted',
    defaultMessage: 'PDF deleted'
  });

  const errorMessage = view.state.errorDeletingExported?.message;

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleDelete}
      screen={screen}
      title={title}
      deleteDescription={deleteDescription}
      thingsToKnow={thingsToKnow}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="delete-exported-pdf-dialog"
    />
  );
};
